import iccService from '@/services/icc/iccService';
import {getFormattedSsoTemplate} from '@/services/icc/sso/formatSsoTemplate';
import {isFailed, SSO_LINK_STATUSES, type SsoLinkStatus} from '@/services/icc/sso/ssoLinkStatuses';
import log from '@/services/logger/log';
import {areSsoCookiesMissing, setSsoCookies} from '@/services/sso/ssoCookieService';
import {getLinkExpiration} from '@/services/sso/ssoExpiration';
import {
    resolveSsoLinkUrlForOpenFromState,
    shouldRequestSessionInfo,
} from '@/services/sso/ssoIccSessionInfoPolicy.wired';
import {selectGigyaToken, selectIsDcxMarket} from '@/state/selectors/auth';
import {makeSelectSsoLink} from '@/state/selectors/media';
import type {AddSsoLinkPayload} from '@/state/slices/mediaSlice';
import {addSsoTemplateLink, addSsoUrlLink, recordSsoSessionApplied} from '@/state/slices/mediaSlice';
import {dispatch, getState} from '@/state/store';
import type {IccSessionInfo} from '@/types/icc/sessionInfo';
import arrayUtils from '@/utils/arrayUtils';

export type IccSsoGetSsoLinksParams = {
    ssoTemplates?: Array<string | null | undefined>;
    urls?: Array<string | null | undefined>;
    includeSessionInfo?: boolean;
};

export type IccSsoResolveUrlForOpenParams = {
    ssoTemplate?: string;
    ssoParams?: Record<string, unknown>;
    linkProcessor?: (url: string) => string;
    openInBluefy?: boolean;
};

type FetchSsoLinkParams = {
    ssoTemplate: string;
    linkProcessor?: (url: string) => string;
};

type GetSsoLinkParams = {
    ssoTemplate?: string;
    params?: Record<string, unknown>;
    linkProcessor?: (url: string) => string;
};

type LoginTokenListEntry = {
    id?: string;
    url?: string;
    directUrl?: string;
    expirationDate?: number;
};

type BatchRequestNameItem = {autoLoginLinkName: string};
type BatchRequestUrlItem = {id: string; url: string};

type FetchSSOLinkIccResult = {
    url?: string;
    expirationDate?: number;
    errorStatus?: unknown;
};

type FetchSSOLinksIccResult = {
    autoLoginLinkNames?: Array<LoginTokenListEntry | undefined>;
    autoLoginLinkUrls?: Array<LoginTokenListEntry | undefined>;
    errorStatus?: unknown;
    sessionInfo?: IccSessionInfo | null;
};

type SsoLinkDispatchAction = (payload: AddSsoLinkPayload) => unknown;

// Serialize only the cookie-capable batch request (IncludeSessionInfo=true).
// This prevents multiple concurrent requests from racing cookie updates.
let includeSessionInfoInFlight: Promise<string[] | false | undefined> | null = null;

const performSsoLinksRequest = async (
    requestParams: Pick<IccSsoGetSsoLinksParams, 'ssoTemplates' | 'urls'> & {includeSessionInfo: boolean}
): Promise<string[] | false | undefined> => {
    try {
        const {ssoTemplates = [], urls = [], includeSessionInfo} = requestParams;
        const filteredTemplates = arrayUtils.filterEmptyObjects(ssoTemplates) as string[];
        const filteredUrls = arrayUtils.filterEmptyObjects(urls) as string[];

        if (!filteredTemplates?.length && !filteredUrls?.length) return;

        const setSsoStatus = (status: SsoLinkStatus) => {
            filteredTemplates.forEach((template) => {
                dispatch!(addSsoTemplateLink({ssoTemplate: template, status}));
            });

            filteredUrls.forEach((url) => {
                dispatch!(addSsoUrlLink({ssoTemplate: url, status}));
            });
        };

        try {
            setSsoStatus(SSO_LINK_STATUSES.LOADING);

            const linkNames = filteredTemplates.map((template) => ({
                autoLoginLinkName: template,
            }));

            let i = 0;
            const linkUrls = filteredUrls.map((url) => ({
                id: (i++).toString(),
                url,
            }));

            const {autoLoginLinkNames, autoLoginLinkUrls, errorStatus, sessionInfo} = (await iccService.fetchSSOLinks(
                linkNames,
                linkUrls,
                {includeSessionInfo}
            )) as FetchSSOLinksIccResult;

            if (errorStatus) {
                setSsoStatus(SSO_LINK_STATUSES.FAILED);
                return;
            }

            if (includeSessionInfo) {
                if (sessionInfo) {
                    setSsoCookies(sessionInfo, selectGigyaToken(getState!()));
                    dispatch!(recordSsoSessionApplied());
                } else {
                    log.info('iccSsoService: getSsoLinks, IncludeSessionInfo was true but SessionInfo was absent');
                }
            }

            const responseUrls: string[] = [];
            const proceedResponse = (
                items: Array<BatchRequestNameItem | BatchRequestUrlItem>,
                response: Array<LoginTokenListEntry | undefined>,
                cacheDispatch: SsoLinkDispatchAction
            ) => {
                items.forEach((item) => {
                    const id = 'id' in item ? item.id : undefined;
                    const autoLoginLinkName = 'autoLoginLinkName' in item ? item.autoLoginLinkName : undefined;
                    const url = 'url' in item ? item.url : undefined;
                    const template = (autoLoginLinkName || url) as string;
                    const linkData = response.find((x) => x?.id === id || x?.id === autoLoginLinkName);
                    const {id: responseId, url: responseUrl, directUrl, expirationDate} = linkData || {};

                    if (!responseId || !responseUrl) {
                        dispatch!(cacheDispatch({ssoTemplate: template, status: SSO_LINK_STATUSES.FAILED}));
                        return;
                    }

                    responseUrls.push(responseUrl);

                    dispatch!(
                        cacheDispatch({
                            ssoTemplate: template,
                            url: responseUrl,
                            directUrl,
                            expirationDate,
                            status: SSO_LINK_STATUSES.SUCCESS,
                        })
                    );
                });
            };

            proceedResponse(linkNames, autoLoginLinkNames ?? [], addSsoTemplateLink);
            proceedResponse(linkUrls, autoLoginLinkUrls ?? [], addSsoUrlLink);

            return responseUrls;
        } catch (e) {
            setSsoStatus(SSO_LINK_STATUSES.FAILED);
            throw e;
        }
    } catch (error) {
        log.info(`iccSsoService: getSsoLinks error: ${error}`);
    }

    return false;
};

// --- Single-token fetch + Redux ---

const fetchSsoLink = async ({ssoTemplate, linkProcessor}: FetchSsoLinkParams): Promise<string | null | undefined> => {
    try {
        dispatch!(addSsoTemplateLink({ssoTemplate, status: SSO_LINK_STATUSES.LOADING}));

        const {url, expirationDate, errorStatus} = (await iccService.fetchSSOLink(
            ssoTemplate
        )) as FetchSSOLinkIccResult;

        if (errorStatus || !url) {
            dispatch!(addSsoTemplateLink({ssoTemplate, status: SSO_LINK_STATUSES.FAILED}));

            return null;
        } else {
            dispatch!(
                addSsoTemplateLink({
                    ssoTemplate,
                    url,
                    expirationDate,
                    status: SSO_LINK_STATUSES.SUCCESS,
                })
            );
        }

        return linkProcessor ? linkProcessor(url) : url;
    } catch (e) {
        dispatch!(addSsoTemplateLink({ssoTemplate, status: SSO_LINK_STATUSES.FAILED}));
        log.info(`iccSsoService: fetchSsoLink error: ${e}`);
    }
};

// --- Read cache + conditional single fetch ---

const getSsoLink = async ({
    ssoTemplate,
    params,
    linkProcessor,
}: GetSsoLinkParams): Promise<string | null | undefined> => {
    const formattedSsoTemplate = getFormattedSsoTemplate({ssoTemplate, params});

    if (!formattedSsoTemplate) return;

    const entry = makeSelectSsoLink(formattedSsoTemplate)(getState!());
    const rawUrl = entry.url;
    const isLinkFetchedAndActive = rawUrl && !getLinkExpiration(entry.expirationDate).isExpired;

    if (isLinkFetchedAndActive) {
        const resolved = resolveSsoLinkUrlForOpenFromState(entry, false);

        return resolved ? (linkProcessor ? linkProcessor(resolved) : resolved) : undefined;
    }

    return fetchSsoLink({ssoTemplate: formattedSsoTemplate, linkProcessor});
};

// --- Batch fetch + Redux ---

const getSsoLinks = async ({
    ssoTemplates = [],
    urls = [],
    includeSessionInfo = false,
}: IccSsoGetSsoLinksParams): Promise<string[] | false | undefined> => {
    // Only serialize includeSessionInfo=true to avoid blocking independent requests.
    if (includeSessionInfo) {
        if (includeSessionInfoInFlight) {
            await includeSessionInfoInFlight;
            includeSessionInfo = areSsoCookiesMissing();
        }

        if (includeSessionInfo) {
            const requestPromise = performSsoLinksRequest({ssoTemplates, urls, includeSessionInfo: true});

            includeSessionInfoInFlight = requestPromise.finally(() => {
                includeSessionInfoInFlight = null;
            });
            return await includeSessionInfoInFlight;
        }
    }

    return await performSsoLinksRequest({ssoTemplates, urls, includeSessionInfo: false});
};

// --- Lazy resolve for openExternalLink ---

const resolveSsoUrlForOpen = async ({
    ssoTemplate,
    ssoParams,
    linkProcessor,
    openInBluefy = false,
}: IccSsoResolveUrlForOpenParams): Promise<string | null | undefined> => {
    const isDcx = selectIsDcxMarket(getState!());

    if (isDcx) {
        const formattedTemplate = getFormattedSsoTemplate({ssoTemplate, params: ssoParams});

        if (!formattedTemplate) return;

        const includeSessionInfo = shouldRequestSessionInfo({
            allowSessionInfo: !openInBluefy,
        });

        await getSsoLinks({
            ssoTemplates: [formattedTemplate],
            urls: [],
            includeSessionInfo,
        });

        const entry = makeSelectSsoLink(formattedTemplate)(getState!());
        const resolved = resolveSsoLinkUrlForOpenFromState(entry, Boolean(openInBluefy));

        return resolved ? (linkProcessor ? linkProcessor(resolved) : resolved) : undefined;
    }

    return getSsoLink({ssoTemplate, params: ssoParams, linkProcessor});
};

const iccSsoService = {
    fetchSsoLink,
    getSsoLink,
    getSsoLinks,
    getFormattedSsoTemplate,
    getLinkExpiration,
    isFailed,
    resolveSsoUrlForOpen,
};

export default iccSsoService;
